/**
 * TransferInvoicingDialog — one-stop panel for a transfer's paperwork.
 *
 * Renders inside the Transfers page. Shows the dispatch note, GRN and
 * settlement invoice (if any), lets admin download PDFs, and — when
 * the transfer is received but not settled — opens a small form for
 * freight/handling overrides + custom charges + billing mode.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  FileText, DownloadSimple, Receipt, Plus, Trash, CheckCircle,
} from '@phosphor-icons/react';
import {
  listTransferDocuments, generateDispatchNote, generateGrn, settleTransfer,
  markSettlementPaid, downloadTransferDocPdf,
} from '../lib/api';

const DOC_META = {
  dispatch_note: { label: 'Dispatch note (OUT)', colour: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  grn: { label: 'Goods received note (IN)', colour: 'text-sky-700 bg-sky-50 border-sky-200' },
  settlement_invoice: { label: 'Settlement invoice', colour: 'text-violet-700 bg-violet-50 border-violet-200' },
};

export default function TransferInvoicingDialog({ open, onClose, transfer, onChange }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSettleForm, setShowSettleForm] = useState(false);
  const [settleBody, setSettleBody] = useState({
    billing_mode: 'internal',
    bill_to_customer_name: '',
    freight_override: '',
    handling_override: '',
    custom_charges: [],
    tax_rate_pct: 0,
    notes: '',
  });

  const refresh = async () => {
    if (!transfer) return;
    setLoading(true);
    try {
      const r = await listTransferDocuments(transfer.id);
      setDocs(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load documents');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) refresh(); }, [open, transfer?.id]);

  const download = async (doc) => {
    try {
      const r = await downloadTransferDocPdf(doc.id);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `${doc.doc_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    }
  };

  const regenerate = async (kind) => {
    try {
      if (kind === 'dispatch_note') await generateDispatchNote(transfer.id);
      else if (kind === 'grn') await generateGrn(transfer.id);
      toast.success('Generated');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate');
    }
  };

  const doSettle = async () => {
    if (settleBody.billing_mode === 'customer' && !settleBody.bill_to_customer_name?.trim()) {
      toast.error('Enter a customer name for customer billing');
      return;
    }
    try {
      const clean = {
        ...settleBody,
        freight_override: settleBody.freight_override === '' ? null : Number(settleBody.freight_override),
        handling_override: settleBody.handling_override === '' ? null : Number(settleBody.handling_override),
        custom_charges: settleBody.custom_charges.filter((c) => c.label && Number(c.amount) > 0).map((c) => ({ label: c.label, amount: Number(c.amount) })),
        tax_rate_pct: Number(settleBody.tax_rate_pct) || 0,
      };
      await settleTransfer(transfer.id, clean);
      toast.success('Settlement invoice created');
      setShowSettleForm(false);
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Settlement failed');
    }
  };

  const markPaid = async (doc) => {
    try {
      await markSettlementPaid(doc.id);
      toast.success('Marked paid');
      await refresh();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not mark paid');
    }
  };

  const has = (t) => docs.some((d) => d.doc_type === t);
  const settlement = docs.find((d) => d.doc_type === 'settlement_invoice');

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="transfer-invoicing-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={20} weight="fill" /> Transfer paperwork · {transfer?.transfer_number}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {transfer?.from_warehouse_code} → {transfer?.to_warehouse_code} · Status <span className="font-bold uppercase">{transfer?.status?.replace('_', ' ')}</span>
          </p>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading documents…</div>
        ) : (
          <div className="space-y-2" data-testid="transfer-docs-list">
            {docs.length === 0 && (
              <div className="text-sm text-muted-foreground border border-dashed border-border p-4 text-center">
                No documents yet. Dispatch notes generate on ship, GRNs on receive.
              </div>
            )}
            {docs.map((d) => (
              <div key={d.id} data-testid={`transfer-doc-${d.doc_type}`}
                className={`border p-3 flex items-center justify-between gap-2 ${DOC_META[d.doc_type]?.colour || ''}`}>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">
                    {DOC_META[d.doc_type]?.label || d.doc_type}
                  </div>
                  <div className="font-mono text-sm font-bold">{d.doc_number}</div>
                  <div className="text-xs opacity-80">
                    {d.doc_type === 'settlement_invoice' && (
                      <>
                        {d.currency} {Number(d.total).toFixed(2)} · <span className="font-semibold uppercase">{d.status}</span>
                        {' · '}{d.billing_mode === 'customer' ? `Bill to: ${d.bill_to_customer_name || '—'}` : 'Internal chargeback'}
                      </>
                    )}
                    {d.doc_type === 'dispatch_note' && (
                      <>{d.total_units} units · {d.currency} {Number(d.total_goods_value).toFixed(2)}</>
                    )}
                    {d.doc_type === 'grn' && (
                      <>
                        Shipped {d.total_shipped} · Received {d.total_received}
                        {d.total_variance !== 0 && (
                          <span className="ml-2 text-rose-700 font-bold">Variance: {d.total_variance > 0 ? '+' : ''}{d.total_variance}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  {d.doc_type === 'settlement_invoice' && d.status !== 'paid' && (
                    <Button size="sm" variant="outline" onClick={() => markPaid(d)} data-testid={`mark-paid-${d.id}`}>
                      <CheckCircle size={14} className="mr-1" /> Mark paid
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => download(d)} data-testid={`download-doc-${d.doc_type}`}>
                    <DownloadSimple size={14} className="mr-1" /> PDF
                  </Button>
                </div>
              </div>
            ))}

            {/* Regenerate buttons if missing but eligible */}
            {transfer?.status !== 'pending' && !has('dispatch_note') && (
              <Button size="sm" variant="outline" onClick={() => regenerate('dispatch_note')} data-testid="gen-dispatch-note-btn">
                <FileText size={14} className="mr-1" /> Generate dispatch note
              </Button>
            )}
            {transfer?.status === 'received' && !has('grn') && (
              <Button size="sm" variant="outline" onClick={() => regenerate('grn')} data-testid="gen-grn-btn">
                <FileText size={14} className="mr-1" /> Generate GRN
              </Button>
            )}

            {/* Settlement form */}
            {transfer?.status === 'received' && !settlement && !showSettleForm && (
              <Button size="sm" onClick={() => setShowSettleForm(true)} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="show-settle-form-btn">
                <Receipt size={14} className="mr-1" /> Generate settlement invoice
              </Button>
            )}
            {showSettleForm && (
              <div className="border border-violet-300 bg-violet-50 p-3 space-y-2" data-testid="settle-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Billing mode</Label>
                    <Select value={settleBody.billing_mode} onValueChange={(v) => setSettleBody({ ...settleBody, billing_mode: v })}>
                      <SelectTrigger data-testid="settle-billing-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="internal">Internal chargeback</SelectItem>
                        <SelectItem value="customer">3PL customer invoice</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {settleBody.billing_mode === 'customer' && (
                    <div>
                      <Label className="text-xs">Bill to (customer name)</Label>
                      <Input value={settleBody.bill_to_customer_name} onChange={(e) => setSettleBody({ ...settleBody, bill_to_customer_name: e.target.value })} data-testid="settle-customer-name" />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Freight override (blank = use rate card)</Label>
                    <Input type="number" value={settleBody.freight_override} onChange={(e) => setSettleBody({ ...settleBody, freight_override: e.target.value })} data-testid="settle-freight" />
                  </div>
                  <div>
                    <Label className="text-xs">Handling override (blank = auto)</Label>
                    <Input type="number" value={settleBody.handling_override} onChange={(e) => setSettleBody({ ...settleBody, handling_override: e.target.value })} data-testid="settle-handling" />
                  </div>
                  <div>
                    <Label className="text-xs">Tax rate %</Label>
                    <Input type="number" value={settleBody.tax_rate_pct} onChange={(e) => setSettleBody({ ...settleBody, tax_rate_pct: e.target.value })} data-testid="settle-tax" />
                  </div>
                </div>

                <div>
                  <Label className="text-xs">Custom charges</Label>
                  {settleBody.custom_charges.map((c, i) => (
                    <div key={i} className="flex gap-1 mt-1">
                      <Input placeholder="Label" value={c.label} onChange={(e) => {
                        const cc = [...settleBody.custom_charges]; cc[i] = { ...cc[i], label: e.target.value };
                        setSettleBody({ ...settleBody, custom_charges: cc });
                      }} data-testid={`custom-charge-label-${i}`} />
                      <Input placeholder="Amount" type="number" className="w-32" value={c.amount} onChange={(e) => {
                        const cc = [...settleBody.custom_charges]; cc[i] = { ...cc[i], amount: e.target.value };
                        setSettleBody({ ...settleBody, custom_charges: cc });
                      }} data-testid={`custom-charge-amount-${i}`} />
                      <Button size="sm" variant="outline" onClick={() => {
                        setSettleBody({ ...settleBody, custom_charges: settleBody.custom_charges.filter((_, ix) => ix !== i) });
                      }}><Trash size={12} /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" className="mt-1" onClick={() => setSettleBody({ ...settleBody, custom_charges: [...settleBody.custom_charges, { label: '', amount: 0 }] })} data-testid="add-custom-charge-btn">
                    <Plus size={12} className="mr-1" /> Add charge
                  </Button>
                </div>

                <div className="flex gap-2 pt-1">
                  <Button size="sm" onClick={doSettle} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="confirm-settle-btn">
                    Create invoice
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowSettleForm(false)}>Cancel</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="close-invoicing-dialog">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
